import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Phone from './Phone';
import BottomNav from './BottomNav';
import DesktopLayout from './layout/DesktopLayout';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import Icon from './Icon';
import { C } from './theme';
import { ToastContainer } from './loyalty/components/ui';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLoyaltyProgramRequest } from '../redux/loyalty/program/loyaltyProgramActions';

const LOYALTY_ROUTE_LABELS = {
    dashboard: 'Dashboard',
    program: 'Program',
    'program/setup': 'Program Setup',
    rewards: 'Rewards',
    members: 'Members',
    // DISABLED: 'members/lapsed-rules': 'Lapsed Rules',
    notifications: 'Notifications',
    'notifications/configure': 'Configure Notification',
    'automated-rewards': 'Automated Rewards',
};

const useIsDesktop = () => {
    const query = '(min-width: 900px)';
    const getMatches = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
    const [isDesktop, setIsDesktop] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const media = window.matchMedia(query);
        const onChange = () => setIsDesktop(media.matches);
        setIsDesktop(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
};

const LoyaltyMobileHeader = ({ title, showBack }: { title: string; showBack?: boolean }) => {
    const navigate = useNavigate();
    return (
        <div style={{ padding: '10px 16px 8px', flexShrink: 0, background: C.surface, borderBottom: `1px solid ${C.divider}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {showBack && (
                    <div
                        onClick={() => navigate(-1)}
                        style={{ width: 32, height: 32, borderRadius: 8, background: C.hover, border: `1px solid ${C.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                    >
                        <span style={{ width: 22, height: 22, borderRadius: 5, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <path d="M7.5 2.5L4 6L7.5 9.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </div>
                )}
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: C.t3 }}>Loyalty {showBack ? '›' : ''}</div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: C.t1 }}>{title}</div>
                </div>
            </div>
        </div>
    );
};

/**
 * Layout for Loyalty section: DesktopLayout (desktop) or Phone + BottomNav (mobile).
 * Renders nested loyalty routes via Outlet (Dashboard, Program, Tiers, etc.).
 * Sets TopBar breadcrumb and title from current loyalty route.
 */
export default function LoyaltyLayout() {
    const isDesktop = useIsDesktop();
    const location = useLocation();
    const mobileMainScrollRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const setTopBarProps = useDesktopTopBar();
    const dispatch = useAppDispatch();
    const { program, loading: programLoading } = useAppSelector((s) => s.loyaltyProgram);

    // Ensure the program status is fetched regardless of which /loyalty/* sub-route is
    // entered first (e.g. a hard refresh on /loyalty/members/:id) — the Sidebar gates
    // Rewards/Members/Notifications on `loyaltyProgram.program` being loaded, and that
    // fetch previously only fired from ProgramSettings' own mount effect.
    useEffect(() => {
        if (program == null && !programLoading) {
            dispatch(fetchLoyaltyProgramRequest());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subPath = (() => {
        const path = (location.pathname || '').replace(/\/$/, '');
        if (!path.startsWith('/loyalty')) return 'dashboard';
        const after = path.replace(/^\/loyalty\/?/, '').replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/|$)/i, '/configure$1').replace(/\/\d+(\/|$)/, '/').replace(/\/$/, '');
        return after || 'dashboard';
    })();
    const pageTitle = LOYALTY_ROUTE_LABELS[subPath] || LOYALTY_ROUTE_LABELS[subPath.split('/')[0]] || 'Dashboard';
    const isDetailPage = /\/loyalty\/(members\/[^/]+|rewards\/[^/]+\/edit)/.test(location.pathname);

    useLayoutEffect(() => {
        const leftAddon = isDetailPage ? (
            <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: C.hover, border: `1px solid ${C.divider}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0,
                }}
            >
                <span style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: C.accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <path d="M7.5 2.5L4 6L7.5 9.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
            </button>
        ) : undefined;
        setTopBarProps({ breadcrumbs: 'Loyalty', title: pageTitle, leftAddon });
    }, [location.pathname, setTopBarProps, pageTitle, isDetailPage, navigate]);

    // Main scroll is inside DesktopLayout (overflow div), not window — reset on route change.
    useLayoutEffect(() => {
        const desktopMain = document.querySelector<HTMLElement>('[data-desktop-main-scroll]');
        if (desktopMain) desktopMain.scrollTop = 0;
        mobileMainScrollRef.current && (mobileMainScrollRef.current.scrollTop = 0);
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname]);

    useEffect(() => {
        return () => setTopBarProps({ breadcrumbs: '', title: 'Dashboard' });
    }, [setTopBarProps]);

    const pageStyle = { padding: 25 };
    // isHome: user is on /loyalty (no sub-path) — show mobile home screen without header/padding
    const isHome = !location.pathname.startsWith('/loyalty/');

    if (isDesktop) {
        return (
            <DesktopLayout>
                <div style={pageStyle}>
                    <Outlet />
                </div>
                <ToastContainer />
            </DesktopLayout>
        );
    }

    return (
        <Phone>
            {!isHome && <LoyaltyMobileHeader title={pageTitle} showBack />}
            <div ref={mobileMainScrollRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {isHome ? <Outlet /> : <div style={pageStyle}><Outlet /></div>}
            </div>
            <BottomNav />
            <ToastContainer />
        </Phone>
    );
}
